import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { z } from "zod"

import { env } from "../env"
import { getRepositoryName } from "../routing"

export type Locale = z.infer<typeof Locale>
const Locale = z.object({ id: z.string(), label: z.string() })

export async function getDefaultLocales(): Promise<Locale[]> {
  const url = new URL("locales", env.PRISMIC_LOCALES_API_BASE_URL)

  return (
    await request(url, { ...requestOptions, okType: GetDefaultLocalesResponse })
  ).results
}

const GetDefaultLocalesResponse = z.object({ results: z.array(Locale) })

export type RepositoryLocale = z.infer<typeof RepositoryLocale>
const RepositoryLocale = Locale.extend({
  customName: z.string().nullable(),
  isMaster: z.boolean(),
})

export async function deleteRepositoryLocale(
  localeId: RepositoryLocale["id"],
): Promise<void> {
  const url = new URL(
    `repository/locales/${localeId}`,
    env.PRISMIC_LOCALES_API_BASE_URL,
  )
  url.searchParams.set("repository", getRepositoryName())

  await request(url, {
    ...requestOptions,
    method: "DELETE",
    okType: z.unknown(),
  })
}

export async function getRepositoryLocales(): Promise<RepositoryLocale[]> {
  const url = new URL("repository/locales", env.PRISMIC_LOCALES_API_BASE_URL)
  url.searchParams.set("repository", getRepositoryName())

  const { results } = await request(url, {
    ...requestOptions,
    okType: GetRepositoryLocalesResponse,
  })
  return results
}

const GetRepositoryLocalesResponse = z.object({
  results: z.array(RepositoryLocale),
})

export type RepositoryLocaleUpsert = Pick<
  RepositoryLocale,
  "id" | "customName" | "isMaster"
>

export function upsertRepositoryLocale(
  repositoryLocaleUpsert: RepositoryLocaleUpsert,
): Promise<RepositoryLocale> {
  const url = new URL("repository/locales", env.PRISMIC_LOCALES_API_BASE_URL)
  url.searchParams.set("repository", getRepositoryName())

  return request(url, {
    ...requestOptions,
    body: repositoryLocaleUpsert,
    method: "POST",
    okType: RepositoryLocale,
  })
}

const requestOptions = {
  credentials: getRequestCredentials(env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY),
}
